import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import {
  isRequisitionTransitionAllowed,
  REQUISITION_LIFECYCLE_HELPER,
  REQUISITION_STATUS_LABELS,
  requisitionStatusEditOptions,
} from '@core/constants/requisitionLifecycle';
import { clientService } from '@core/supabase/services/clientService';
import { requisitionHistoryService } from '@core/supabase/services/requisitionHistoryService';
import { requisitionService } from '@core/supabase/services/requisitionService';
import type { Client, Requisition, RequisitionInsert, RequisitionPriority, RequisitionStatus } from '@core/types';
import { REQUISITION_PRIORITIES } from '@core/enums';
import type { RequisitionAiContext, RequisitionFormAssistPatch } from './requisitionAssistTypes';

export interface RequisitionFormProps {
  mode: 'new' | 'edit';
  requisition?: Requisition | null;
  readOnly?: boolean;
  orgId: string;
  userId: string;
  onSave: (r: Requisition) => void;
  /** When status changes on save, append REQ-050 history (edit mode). */
  historyActor?: { displayName: string; orgUserId: string | null };
  /** Merge AI / template fields into the form (consumed once per non-null value). */
  pendingAssist?: RequisitionFormAssistPatch | null;
  onPendingAssistConsumed?: () => void;
  /** Debounced snapshot for AI / tabs (optional). */
  onDraftSnapshot?: (ctx: RequisitionAiContext) => void;
}

export function RequisitionForm({
  mode,
  requisition,
  readOnly = false,
  orgId,
  userId,
  onSave,
  historyActor,
  pendingAssist,
  onPendingAssistConsumed,
  onDraftSnapshot,
}: RequisitionFormProps) {
  const disabled = readOnly;
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mustInput, setMustInput] = useState('');
  const [niceInput, setNiceInput] = useState('');
  const [reqAiField, setReqAiField] = useState<null | 'business' | 'internal' | 'title'>(null);

  const [form, setForm] = useState({
    client_id: requisition?.client_id ?? '',
    title: requisition?.title ?? '',
    department: requisition?.department ?? '',
    headcount: requisition?.headcount != null ? String(requisition.headcount) : '1',
    priority: (requisition?.priority ?? 'normal') as RequisitionPriority,
    status: (requisition?.status ?? 'draft') as RequisitionStatus,
    hiring_manager_name: requisition?.hiring_manager_name ?? '',
    hiring_manager_email: requisition?.hiring_manager_email ?? '',
    budget_currency: requisition?.budget_currency ?? 'INR',
    budget_min: requisition?.budget_min != null ? String(requisition.budget_min) : '',
    budget_max: requisition?.budget_max != null ? String(requisition.budget_max) : '',
    location: requisition?.location ?? '',
    employment_type: requisition?.employment_type ?? '',
    business_justification: requisition?.business_justification ?? '',
    internal_notes: requisition?.internal_notes ?? '',
    target_start_date: requisition?.target_start_date?.slice(0, 10) ?? '',
    target_close_date: requisition?.target_close_date?.slice(0, 10) ?? '',
    must_have_skills: [...(requisition?.must_have_skills ?? [])],
    nice_to_have_skills: [...(requisition?.nice_to_have_skills ?? [])],
  });

  useEffect(() => {
    clientService.list(orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
  }, [orgId]);

  useEffect(() => {
    if (requisition) {
      setForm({
        client_id: requisition.client_id ?? '',
        title: requisition.title ?? '',
        department: requisition.department ?? '',
        headcount: String(requisition.headcount ?? 1),
        priority: requisition.priority ?? 'normal',
        status: requisition.status ?? 'draft',
        hiring_manager_name: requisition.hiring_manager_name ?? '',
        hiring_manager_email: requisition.hiring_manager_email ?? '',
        budget_currency: requisition.budget_currency ?? 'INR',
        budget_min: requisition.budget_min != null ? String(requisition.budget_min) : '',
        budget_max: requisition.budget_max != null ? String(requisition.budget_max) : '',
        location: requisition.location ?? '',
        employment_type: requisition.employment_type ?? '',
        business_justification: requisition.business_justification ?? '',
        internal_notes: requisition.internal_notes ?? '',
        target_start_date: requisition.target_start_date?.slice(0, 10) ?? '',
        target_close_date: requisition.target_close_date?.slice(0, 10) ?? '',
        must_have_skills: [...(requisition.must_have_skills ?? [])],
        nice_to_have_skills: [...(requisition.nice_to_have_skills ?? [])],
      });
    }
  }, [requisition?.id]);

  /** §9.3 / REQ-094: new rows must be draft — client gate on detail advances workflow. */
  const statusSelectOptions = useMemo((): RequisitionStatus[] => {
    if (mode === 'new') return ['draft'];
    return requisitionStatusEditOptions(form.status);
  }, [mode, form.status]);

  const assistDoneRef = useRef(onPendingAssistConsumed);
  assistDoneRef.current = onPendingAssistConsumed;

  const snapshotRef = useRef(onDraftSnapshot);
  snapshotRef.current = onDraftSnapshot;

  useEffect(() => {
    if (!snapshotRef.current) return;
    const t = window.setTimeout(() => {
      const clientName = clients.find((c) => c.id === form.client_id)?.name;
      snapshotRef.current?.({
        title: form.title,
        department: form.department,
        clientName,
        employment_type: form.employment_type,
        location: form.location,
        business_justification: form.business_justification,
        must_have_skills: form.must_have_skills,
        nice_to_have_skills: form.nice_to_have_skills,
      });
    }, 320);
    return () => clearTimeout(t);
  }, [
    clients,
    form.client_id,
    form.title,
    form.department,
    form.employment_type,
    form.location,
    form.business_justification,
    form.internal_notes,
    form.must_have_skills,
    form.nice_to_have_skills,
  ]);

  useEffect(() => {
    if (!pendingAssist) return;
    setForm((f) => {
      const next = { ...f };
      if (pendingAssist.title != null) next.title = pendingAssist.title;
      if (pendingAssist.department != null) next.department = pendingAssist.department;
      if (pendingAssist.employment_type != null) next.employment_type = pendingAssist.employment_type;
      if (pendingAssist.location != null) next.location = pendingAssist.location;
      if (pendingAssist.business_justification != null) next.business_justification = pendingAssist.business_justification;
      if (pendingAssist.internal_notes != null) next.internal_notes = pendingAssist.internal_notes;
      if (pendingAssist.must_have_skills?.length) {
        const merged = [...next.must_have_skills];
        for (const s of pendingAssist.must_have_skills) {
          if (!merged.some((x) => x.toLowerCase() === s.toLowerCase())) merged.push(s);
        }
        next.must_have_skills = merged;
      }
      if (pendingAssist.nice_to_have_skills?.length) {
        const merged = [...next.nice_to_have_skills];
        for (const s of pendingAssist.nice_to_have_skills) {
          if (!merged.some((x) => x.toLowerCase() === s.toLowerCase())) merged.push(s);
        }
        next.nice_to_have_skills = merged;
      }
      return next;
    });
    assistDoneRef.current?.();
  }, [pendingAssist]);

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const addMust = () => {
    const s = mustInput.trim();
    if (s && !form.must_have_skills.includes(s)) {
      setForm((f) => ({ ...f, must_have_skills: [...f.must_have_skills, s] }));
    }
    setMustInput('');
  };

  const addNice = () => {
    const s = niceInput.trim();
    if (s && !form.nice_to_have_skills.includes(s)) {
      setForm((f) => ({ ...f, nice_to_have_skills: [...f.nice_to_have_skills, s] }));
    }
    setNiceInput('');
  };

  const toInsert = (submittedAt: string | null, approvedAt: string | null): RequisitionInsert => ({
    org_id: orgId,
    client_id: form.client_id,
    created_by: mode === 'new' ? userId : requisition!.created_by,
    title: form.title.trim(),
    department: form.department.trim() || null,
    headcount: Math.max(1, parseInt(form.headcount, 10) || 1),
    priority: form.priority,
    status: form.status,
    hiring_manager_name: form.hiring_manager_name.trim() || null,
    hiring_manager_email: form.hiring_manager_email.trim() || null,
    budget_currency: form.budget_currency.trim() || 'INR',
    budget_min: form.budget_min ? parseInt(form.budget_min, 10) : null,
    budget_max: form.budget_max ? parseInt(form.budget_max, 10) : null,
    location: form.location.trim() || null,
    employment_type: form.employment_type.trim() || null,
    business_justification: form.business_justification.trim() || null,
    internal_notes: form.internal_notes.trim() || null,
    target_start_date: form.target_start_date || null,
    target_close_date: form.target_close_date || null,
    must_have_skills: form.must_have_skills,
    nice_to_have_skills: form.nice_to_have_skills,
    submitted_at: submittedAt,
    approved_at: approvedAt,
    job_id: requisition?.job_id ?? null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.client_id) {
      setError('Select a client');
      return;
    }
    if (mode === 'new' && !form.target_close_date) {
      setError('Target close date is required');
      return;
    }
    if (form.target_close_date && form.target_close_date <= new Date().toISOString().slice(0, 10)) {
      setError('Target close must be in the future.');
      return;
    }
    const selectedClient = clients.find((c) => c.id === form.client_id);
    if (mode === 'new' && !isClientReadyForRecruitment(selectedClient)) {
      setError(
        'This client is still in onboarding. Complete compliance on the client page before creating a requisition.'
      );
      return;
    }
    if (mode === 'edit' && requisition && requisition.status !== form.status) {
      if (!isRequisitionTransitionAllowed(requisition.status, form.status)) {
        setError(
          `Invalid status change (${requisition.status} → ${form.status}). Use Overview lifecycle actions or follow the allowed sequence (PRD §9.3).`
        );
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      let submittedAt = requisition?.submitted_at ?? null;
      let approvedAt = requisition?.approved_at ?? null;
      if (form.status === 'submitted' && !submittedAt) submittedAt = new Date().toISOString();
      if (form.status === 'approved' && !approvedAt) approvedAt = new Date().toISOString();

      if (mode === 'new') {
        const created = await requisitionService.create({
          ...toInsert(null, null),
          status: 'draft',
          submitted_at: null,
          approved_at: null,
          rejected_at: null,
        } as RequisitionInsert);
        onSave(created);
      } else if (requisition) {
        const { org_id: _o, created_by: _c, ...rest } = toInsert(submittedAt, approvedAt);
        let rejected_at = requisition.rejected_at ?? null;
        if (form.status === 'rejected' && requisition.status !== 'rejected') {
          rejected_at = new Date().toISOString();
        }
        const updated = await requisitionService.update(requisition.id, { ...rest, rejected_at });
        if (historyActor && requisition.status !== updated.status) {
          await requisitionHistoryService.record(orgId, requisition.id, requisition.status, updated.status, {
            changedBy: historyActor.displayName,
            changedById: historyActor.orgUserId,
          });
        }
        onSave(updated);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const clientNameForAi = clients.find((c) => c.id === form.client_id)?.name ?? '';

  return (
    <>
    <form id="requisition-form" onSubmit={handleSubmit} className="space-y-6">
      {error && <p className="text-sm text-red-400">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Client *</label>
          <select
            className="input w-full"
            value={form.client_id}
            onChange={set('client_id')}
            disabled={disabled || mode === 'edit'}
            required
          >
            <option value="">Select client</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <label className="block text-xs text-gray-400 mb-0">Role title *</label>
            {!disabled && (
              <AiAssistIconButton onClick={() => setReqAiField('title')} title="AI assist — role title" />
            )}
          </div>
          <input className="input w-full" value={form.title} onChange={set('title')} disabled={disabled} required />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Department</label>
          <input className="input w-full" value={form.department} onChange={set('department')} disabled={disabled} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Headcount</label>
          <input
            type="number"
            min={1}
            className="input w-full"
            value={form.headcount}
            onChange={set('headcount')}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Priority</label>
          <select
            className="input w-full"
            value={form.priority}
            onChange={set('priority')}
            disabled={disabled}
          >
            {REQUISITION_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          {mode === 'new' ? (
            <>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <p className="text-sm text-gray-300 rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2 max-w-xl">
                <strong className="text-white">{REQUISITION_STATUS_LABELS.draft}</strong>
                <span className="text-gray-500"> — </span>
                After you save, open <strong className="text-gray-200">Overview</strong> and use{' '}
                <strong className="text-gray-200">Hiring client gate</strong> to email the employer before internal approval
                (PRD §9.3).
              </p>
            </>
          ) : (
            <>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select className="input w-full max-w-md" value={form.status} onChange={set('status')} disabled={disabled}>
                {statusSelectOptions.map((s) => (
                  <option key={s} value={s}>
                    {REQUISITION_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              {!disabled ? (
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed max-w-xl">{REQUISITION_LIFECYCLE_HELPER}</p>
              ) : null}
            </>
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Hiring manager</label>
          <input
            className="input w-full"
            value={form.hiring_manager_name}
            onChange={set('hiring_manager_name')}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Hiring manager email</label>
          <input
            type="email"
            className="input w-full"
            value={form.hiring_manager_email}
            onChange={set('hiring_manager_email')}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Location</label>
          <input className="input w-full" value={form.location} onChange={set('location')} disabled={disabled} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Employment type</label>
          <input
            className="input w-full"
            placeholder="e.g. Full-time, Contract"
            value={form.employment_type}
            onChange={set('employment_type')}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Target start date</label>
          <input type="date" className="input w-full" value={form.target_start_date} onChange={set('target_start_date')} disabled={disabled} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Target close date {mode === 'new' ? <span className="text-red-400">*</span> : null}
          </label>
          <input
            type="date"
            className="input w-full"
            value={form.target_close_date}
            min={new Date().toISOString().slice(0, 10)}
            onChange={set('target_close_date')}
            disabled={disabled}
            required={mode === 'new'}
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Deadline for filling this position. Defaults to 30 days from approval.
          </p>
        </div>
        <div className="flex gap-2 sm:col-span-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Budget min ({form.budget_currency})</label>
            <input type="number" className="input w-full" value={form.budget_min} onChange={set('budget_min')} disabled={disabled} />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Budget max</label>
            <input type="number" className="input w-full" value={form.budget_max} onChange={set('budget_max')} disabled={disabled} />
          </div>
          <div className="w-24">
            <label className="block text-xs text-gray-400 mb-1">Currency</label>
            <input className="input w-full" value={form.budget_currency} onChange={set('budget_currency')} disabled={disabled} />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <label className="block text-xs text-gray-400 mb-0">Business justification / role summary</label>
          {!disabled && (
            <AiAssistIconButton onClick={() => setReqAiField('business')} title="AI assist — business justification" />
          )}
        </div>
        <textarea
          className="input w-full min-h-[100px]"
          value={form.business_justification}
          onChange={set('business_justification')}
          disabled={disabled}
        />
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Must-have skills</label>
        <div className="flex gap-2 mb-2">
          <input
            className="input flex-1"
            value={mustInput}
            onChange={(e) => setMustInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMust())}
            disabled={disabled}
            placeholder="Add skill, Enter"
          />
          <button type="button" className="btn-secondary" onClick={addMust} disabled={disabled}>
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {form.must_have_skills.map((s) => (
            <button
              key={s}
              type="button"
              className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-200"
              onClick={() => !disabled && setForm((f) => ({ ...f, must_have_skills: f.must_have_skills.filter((x) => x !== s) }))}
              disabled={disabled}
            >
              {s} ×
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Nice-to-have skills</label>
        <div className="flex gap-2 mb-2">
          <input
            className="input flex-1"
            value={niceInput}
            onChange={(e) => setNiceInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNice())}
            disabled={disabled}
            placeholder="Add skill, Enter"
          />
          <button type="button" className="btn-secondary" onClick={addNice} disabled={disabled}>
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {form.nice_to_have_skills.map((s) => (
            <button
              key={s}
              type="button"
              className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-200"
              onClick={() => !disabled && setForm((f) => ({ ...f, nice_to_have_skills: f.nice_to_have_skills.filter((x) => x !== s) }))}
              disabled={disabled}
            >
              {s} ×
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <label className="block text-xs text-gray-400 mb-0">Internal notes (not shared with client)</label>
          {!disabled && (
            <AiAssistIconButton onClick={() => setReqAiField('internal')} title="AI assist — internal notes" />
          )}
        </div>
        <textarea className="input w-full min-h-[80px]" value={form.internal_notes} onChange={set('internal_notes')} disabled={disabled} />
      </div>

      {!disabled && (
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null} Save requisition
        </button>
      )}
    </form>

    {reqAiField === 'title' && (
      <ContextAiAssistModal
        open
        onClose={() => setReqAiField(null)}
        orgId={orgId}
        feature="ai_assist_requisition_role_title"
        preset="requisition_role_title"
        title="AI — role title"
        contextHint={`Client: ${clientNameForAi || '—'}\nLocation: ${form.location}\nMust-have: ${form.must_have_skills.join(', ')}`}
        original={form.title}
        allowAppend={false}
        onApply={(text) => {
          const line = text.trim().split(/\r?\n/)[0]?.trim() || text.trim();
          setForm((f) => ({ ...f, title: line.slice(0, 200) }));
        }}
      />
    )}
    {reqAiField === 'business' && (
      <ContextAiAssistModal
        open
        onClose={() => setReqAiField(null)}
        orgId={orgId}
        feature="ai_assist_requisition_business_justification"
        preset="requisition_business_justification"
        title="AI — business justification"
        contextHint={`Role: ${form.title}\nClient: ${clientNameForAi || '—'}\nLocation: ${form.location}\nMust-have: ${form.must_have_skills.join(', ')}`}
        original={form.business_justification}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            business_justification:
              mode === 'replace'
                ? text
                : f.business_justification.trim()
                  ? `${f.business_justification.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    )}
    {reqAiField === 'internal' && (
      <ContextAiAssistModal
        open
        onClose={() => setReqAiField(null)}
        orgId={orgId}
        feature="ai_assist_requisition_internal_notes"
        preset="requisition_internal_notes"
        title="AI — requisition internal notes"
        contextHint={`Role: ${form.title}\nClient: ${clientNameForAi || '—'}`}
        original={form.internal_notes}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            internal_notes:
              mode === 'replace'
                ? text
                : f.internal_notes.trim()
                  ? `${f.internal_notes.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    )}
    </>
  );
}
